import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import FloatingPapicoo from "@/components/FloatingPapicoo";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { changeLanguage } from "@/i18n";
import { debugLog } from "../lib/logger";
import { MUSIC_RELEASES } from "../data/music";
import { BrandLockup, BrandMark } from "./BrandMark";
import { T } from "@/locales/tr";

const LANGS = [
  { code: "fr", label: "FR", full: T("Français") },
  { code: "en", label: "EN", full: "English" },
  { code: "ar", label: "ع", full: "العربية" },
  { code: "wo", label: "WO", full: "Wolof" },
];

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const active = LANGS.find((l) => l.code === i18n.language) || LANGS[0];
  return (
    <div className="relative" data-testid="lang-switcher">
      <button
        data-testid="lang-switcher-btn"
        onClick={() => setOpen((s) => !s)}
        aria-label={T("Changer de langue")}
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 text-xs text-[color:var(--papicoo-ink)] hover:border-[color:var(--papicoo-gold)] transition-colors"
      >
        <Globe size={12} className="text-[color:var(--papicoo-gold)]" />
        <span className="font-medium">{T(active.label)}</span>
      </button>
      {open && (
        <div data-testid="lang-menu" className="absolute right-0 mt-2 papicoo-card p-1 min-w-[160px] z-50 shadow-xl">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-${l.code}`}
              onClick={() => { changeLanguage(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${i18n.language === l.code ? "bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)]" : "hover:bg-[color:var(--papicoo-sand-deep)]/60"}`}
            >
              {T(l.label)} · {T(l.full)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const navCategories = [
  {
    key: "create",
    label: "Créer",
    color: "text-[color:var(--papicoo-gold)]",
    items: [
      { to: "/studio", label: "Studio", testid: "nav-studio", desc: T("Transformation pro + éveil") },
      { to: "/portrait", label: "Portrait", testid: "nav-portrait", desc: T("5 styles présidentiels IA") },
      { to: "/evoto", label: "Évoto", testid: "nav-evoto", desc: "Recettes retouche" },
      { to: "/retouche", label: "Retouche Pro", testid: "nav-retouche", desc: "Service retouche IA" },
      { to: "/decoder", label: "Décodeur", testid: "nav-decoder", desc: T("4 lectures cachées") },
      { to: "/analyse", label: "Analyse", testid: "nav-analyse", desc: T("Vidéo · réseaux sociaux") },
      { to: "/voice-gift", label: "Message vocal", testid: "nav-voicegift", desc: "Offre ma voix (cadeau MP3)" },
    ],
  },
  {
    key: "diaspora",
    label: "Diaspora",
    color: "text-[color:var(--papicoo-accent)]",
    items: [
      { to: "/gp", label: "GP Sénégal", testid: "nav-gp", desc: "Marketplace voyageurs" },
      { to: "/transfer", label: "Transfert", testid: "nav-transfer", desc: "Stripe diaspora" },
      { to: "/dropship", label: "Boutique", testid: "nav-dropship", desc: "Dropshipping" },
      { to: "/contrats", label: "Contrats", testid: "nav-contracts", desc: T("Templates équitables") },
      { to: "/devis-express", label: "Devis Express", testid: "nav-devis", desc: T("Devis IA personnalisé 19€") },
      { to: "/facebook", label: "Facebook", testid: "nav-facebook", desc: "Posts + commentaires" },
    ],
  },
  {
    key: "spirit",
    label: "Spirituel",
    color: "text-emerald-700",
    items: [
      { to: "/coran", label: "Coran", testid: "nav-coran", desc: "114 sourates · 6236 versets" },
      { to: "/verset", label: "Verset du jour", testid: "nav-verset", desc: "Audio + image 1080×1080" },
      { to: "/ramadan", label: "Ramadan", testid: "nav-ramadan", desc: T("30 lumières du mois béni") },
      { to: "/live", label: "Copilote Live", testid: "nav-live", desc: T("Cheikh Mbacké Sakho") },
      { to: "/nihma-tv", label: "Nihma TV", testid: "nav-nihma", desc: "Transcriptions Whisper" },
    ],
  },
  {
    key: "you",
    label: "Identité",
    color: "text-[color:var(--papicoo-ink)]",
    items: [
      { to: "/", label: "Accueil", end: true, testid: "nav-home", desc: "Force tranquille" },
      { to: "/services", label: "Services", testid: "nav-services", desc: "Mes 11 portes" },
      { to: "/chat", label: "Chat Papicoo", testid: "nav-chat", desc: "Discussion live (3 gratuits)" },
      { to: "/reseaux", label: "Mes Réseaux", testid: "nav-reseaux", desc: "IG · FB · TikTok · WhatsApp" },
      { to: "/papicoo", label: "À propos", testid: "nav-about", desc: T("Qui est Papicoo ?") },
      // Visible uniquement quand des sorties réelles sont renseignées
      ...(MUSIC_RELEASES.length > 0 ? [{ to: "/musique", label: "Musique", testid: "nav-music", desc: T("Les sorties Papicoo") }] : []),
    ],
  },
];

// Flatten for embed/mobile flat list usage (compat ascendante)
const navItems = navCategories.flatMap((c) => c.items);

const QURAN_ROUTES = ["/coran", "/verset", "/ramadan", "/contrats", "/papicoo", "/analyse"];

// Signale honnêtement les traductions incomplètes dans la langue choisie
function TranslationNotice({ pathname }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const msg = lng === "ar" || lng === "wo" ? t("partial")
    : lng === "en" && QURAN_ROUTES.some((r) => pathname.startsWith(r)) ? t("quranFr") : "";
  if (!msg) return null;
  return (
    <div role="note" data-testid="translation-notice" className="border-b border-white/10 bg-[#141416] px-5 py-2.5 text-xs text-[color:var(--papicoo-muted)] text-center">
      {msg}
    </div>
  );
}

export default function Layout() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  // Mode embed : masque header/footer/flottants quand le site est intégré dans un iframe (papicoo.fr/studio)
  const isEmbed = new URLSearchParams(search).get("embed") === "1" || typeof window !== "undefined" && window !== window.parent;

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  useEffect(() => { setOpen(false); }, [pathname]);

  if (isEmbed) {
    return (
      <div className="App min-h-screen overflow-x-hidden bg-[color:var(--papicoo-sand)] text-[color:var(--papicoo-ink)]">
        <main>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col overflow-x-hidden">
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#0b0b0c]/85 border-b border-white/10"
        dir="ltr"
      >
        <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-logo" aria-label={T("Papicoo — accueil")} className="flex items-center gap-3 group shrink-0">
            <BrandLockup baseline={T("Paris — Dakar — Le Monde")} />
          </Link>

          {/* center spacer / breadcrumbs reserved */}
          <div className="flex-1" />

          {/* right side : language switcher + CTA + menu button (all screens) */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link
              to="/chat"
              data-testid="cta-open-chat"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.16em] papicoo-btn-primary"
            >
              {T("Parler à Papicoo")}
            </Link>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="menu-trigger"
                  aria-label={T("Ouvrir le menu")}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/25 text-[color:var(--papicoo-ink)] hover:border-[color:var(--papicoo-gold)] hover:text-[color:var(--papicoo-gold)] transition-colors group"
                >
                  <Menu size={16} />
                  <span className="text-xs font-medium uppercase tracking-[0.2em]">{T("Menu")}</span>
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                data-testid="menu-panel"
                className="w-full sm:w-[480px] md:w-[560px] lg:w-[680px] xl:w-[820px] sm:max-w-none bg-[#0b0b0c] text-[color:var(--papicoo-ink)] border-l border-white/10 p-0 overflow-y-auto"
              >
                <VisuallyHidden>
                  <SheetTitle>{T("Menu Papicoo")}</SheetTitle>
                  <SheetDescription>{T("Navigation principale de l'application Papicoo organisée en 4 univers.")}</SheetDescription>
                </VisuallyHidden>
                {/* Header du panneau */}
                <div className="sticky top-0 z-10 bg-[#0b0b0c]/95 backdrop-blur-xl px-6 py-5 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="font-display text-xl font-semibold tracking-[0.18em]">{T("PAPICOO")}</div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--papicoo-gold)] mt-1">{T("Paris — Dakar — Le Monde")}</div>
                    </div>
                  </div>
                </div>

                {/* Catégories */}
                <div className="p-5 sm:p-6 grid gap-5 sm:grid-cols-2">
                  {navCategories.map((cat) => (
                    <div key={cat.key} className="border-t border-white/10 pt-4">
                      <div className="papicoo-kicker mb-3">{T(cat.label)}</div>
                      <div className="flex flex-col gap-0.5">
                        {cat.items.map((it) => (
                          <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            data-testid={it.testid}
                            className={({ isActive }) =>
                              `group flex items-baseline gap-2 px-3 py-2 border-l-2 transition-colors ${isActive ? "border-[color:var(--papicoo-gold)] bg-white/5 text-[color:var(--papicoo-gold)]" : "border-transparent hover:border-white/30 hover:bg-white/5 text-[color:var(--papicoo-ink)]"}`
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <span className="text-sm font-medium leading-tight">{T(it.label)}</span>
                                {it.desc && (
                                  <span className="text-[11px] leading-tight text-[color:var(--papicoo-muted)]">
                                    · {T(it.desc)}
                                  </span>
                                )}
                              </>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA + Signature */}
                <div className="px-5 sm:px-6 pb-6 space-y-4">
                  <Link
                    to="/chat"
                    data-testid="cta-open-chat-menu"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.18em] papicoo-btn-primary"
                  >
                    {T("Parler à Papicoo")}
                  </Link>
                  <div className="text-center pt-2 border-t border-white/10">
                    <div className="signature-script text-2xl text-[color:var(--papicoo-ink)] mt-3">{T("Design by Papicoo")}</div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[color:var(--papicoo-muted)] mt-1 italic">{T("paix mondiale 🌹")}</div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-x-hidden">
        <TranslationNotice pathname={pathname} />
        <Outlet />
      </main>

      {pathname !== "/chat" && !["/musique", "/verset", "/coran"].some((r) => pathname.startsWith(r)) && <FloatingPapicoo />}
      {!["/verset", "/coran"].some((r) => pathname.startsWith(r)) && <FloatingWhatsApp source={pathname === "/" ? "landing" : pathname.replace("/", "") || "site"} />}

      <footer data-testid="site-footer" className="mt-24 border-t border-white/10 bg-[#0a0a0b]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-3 items-start">
          <div>
            <div className="flex items-center gap-3 text-[color:var(--papicoo-gold)]">
              <BrandMark className="w-8 h-8" />
              <div className="font-display text-2xl font-semibold tracking-[0.18em] text-[color:var(--papicoo-ink)]">{T("PAPICOO")}</div>
            </div>
            <div className="papicoo-kicker mt-2">{T("Paris — Dakar — Le Monde")}</div>
            <p className="text-sm text-[color:var(--papicoo-muted)] mt-2 max-w-xs leading-relaxed">
              {t("footer.intro")}
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <a data-testid="footer-link-website" href="https://www.papicoo.fr" target="_blank" rel="noopener noreferrer" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{T("papicoo.fr")}</a>
              <a data-testid="footer-link-instagram" href="https://www.instagram.com/papicoo75" target="_blank" rel="noopener noreferrer" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{T("Instagram")}</a>
              <a data-testid="footer-link-facebook" href="https://www.facebook.com/share/1LZmopLS3k/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{T("Facebook")}</a>
              <a data-testid="footer-link-tiktok" href="https://www.tiktok.com/@www.papicoo.fr" target="_blank" rel="noopener noreferrer" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{T("TikTok")}</a>
              <a
                data-testid="footer-link-whatsapp"
                href="https://wa.me/33753827747?text=Salaam%20Papicoo"
                onClick={() => { try { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/click`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ source: "footer" }) }); } catch (e) { debugLog("whatsapp click tracker failed:", e?.message); } }}
                target="_blank"
                rel="noopener noreferrer"
                className="papicoo-link-underline text-[color:var(--papicoo-ink)]"
              >{T("WhatsApp Business")}</a>
              <a data-testid="footer-link-library" href="/coran" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{t("footer.library")}</a>
              <Link to="/musique" data-testid="footer-link-music" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{T("Musique")}</Link>
              <a data-testid="footer-link-email" href="mailto:papicoo@papicoo.fr" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">papicoo@papicoo.fr</a>
            </div>
          </div>
          <div className="text-sm text-[color:var(--papicoo-muted)] space-y-2">
            <div className="papicoo-kicker">{t("footer.secTitle")}</div>
            <p>{t("footer.secBody")}</p>
          </div>
          <div className="md:text-right">
            <div className="signature-script text-xl text-[color:var(--papicoo-ink)]">
              {t("footer.signature")}
            </div>
            <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">
              
            </div>
            <div className="mt-3 flex flex-wrap gap-3 justify-start md:justify-end text-xs">
              <Link to="/confidentialite" data-testid="footer-link-privacy" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{t("footer.privacy")}</Link>
              <Link to="/cgu" data-testid="footer-link-cgu" className="papicoo-link-underline text-[color:var(--papicoo-ink)]">{t("footer.cgu")}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
